import { Button } from "components/DesignSystem/Button/Button";
import { Header } from "components/DesignSystem/Header/Header";
import { DashboardLayout } from "components/DashboardLayout";
import Modal from "components/DesignSystem/Modal/Modal";
import { CopyIcon } from "components/icons/CopyIcon";
import { useToast } from "hooks/useToast";
import { usePageTitle } from "hooks/usePageTitle";
import { useState } from "react";
import {
  useGetApiTokenQuery,
  useGenerateApiTokenMutation,
  useRevokeApiTokenMutation,
} from "store/apis/apiToken";
import dayjs from "dayjs";
import { DISPLAY_DATE } from "constants/date";

const TOKEN_PLACEHOLDER = "••••••••••••••••••••••••••••••••••••••••••";

type GeneratedTokenBannerProps = {
  token: string;
  onDismiss: () => void;
};

const GeneratedTokenBanner = ({
  token,
  onDismiss,
}: GeneratedTokenBannerProps) => {
  const { successToast } = useToast();

  const handleCopy = () => {
    navigator.clipboard.writeText(token);
    successToast({ title: "Copied", message: "API token copied to clipboard" });
  };

  return (
    <div className="t-rounded-xl t-border t-border-solid t-border-yellow-20 t-bg-yellow-0 t-p-5 t-flex t-flex-col t-gap-4">
      <div className="t-flex t-flex-col t-gap-1">
        <div className="t-text-body-sm t-font-semibold t-text-text-100">
          Your API token has been generated
        </div>
        <div className="t-text-body-sm t-text-red">
          Copy and store this token now — it will not be shown again.
        </div>
      </div>
      <div className="t-flex t-items-center t-gap-2">
        <div className="t-flex-1 t-rounded-lg t-border t-border-solid t-border-neutral-10 t-bg-white t-px-3 t-py-2 t-font-mono t-text-body-sm t-text-text-100 t-break-all">
          {token}
        </div>
        <Button
          customType="secondary"
          size="small"
          rightIcon={<CopyIcon />}
          onClick={handleCopy}
        >
          Copy
        </Button>
      </div>
      <div className="t-flex t-justify-end">
        <Button customType="primary" size="small" onClick={onDismiss}>
          Done, I've saved it
        </Button>
      </div>
    </div>
  );
};

type RevokeConfirmModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isLoading: boolean;
};

const RevokeConfirmModal = ({
  open,
  onOpenChange,
  onConfirm,
  isLoading,
}: RevokeConfirmModalProps) => (
  <Modal.Root open={open} onOpenChange={onOpenChange}>
    <Modal.Content>
      <Modal.Header>
        <Modal.Title>Revoke API token</Modal.Title>
        <Modal.Close />
      </Modal.Header>
      <Modal.Body>
        <div className="t-text-body-sm t-text-text-60">
          Revoking this token will immediately invalidate it. Any integration
          using this token will stop working. This action cannot be undone.
        </div>
      </Modal.Body>
      <Modal.Footer className="t-flex t-justify-end t-gap-3">
        <Button
          customType="secondary"
          size="small"
          onClick={() => onOpenChange(false)}
          type="button"
        >
          Cancel
        </Button>
        <Button
          customType="danger"
          size="small"
          isLoading={isLoading}
          disabled={isLoading}
          onClick={onConfirm}
          type="button"
        >
          Revoke token
        </Button>
      </Modal.Footer>
    </Modal.Content>
  </Modal.Root>
);

type RegenerateConfirmModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isLoading: boolean;
};

const RegenerateConfirmModal = ({
  open,
  onOpenChange,
  onConfirm,
  isLoading,
}: RegenerateConfirmModalProps) => (
  <Modal.Root open={open} onOpenChange={onOpenChange}>
    <Modal.Content>
      <Modal.Header>
        <Modal.Title>Regenerate API token</Modal.Title>
        <Modal.Close />
      </Modal.Header>
      <Modal.Body>
        <div className="t-text-body-sm t-text-text-60">
          Regenerating will revoke your existing token and issue a new one. Any
          integration using the current token will stop working immediately.
        </div>
      </Modal.Body>
      <Modal.Footer className="t-flex t-justify-end t-gap-3">
        <Button
          customType="secondary"
          size="small"
          onClick={() => onOpenChange(false)}
          type="button"
        >
          Cancel
        </Button>
        <Button
          customType="primary"
          size="small"
          isLoading={isLoading}
          disabled={isLoading}
          onClick={onConfirm}
          type="button"
        >
          Regenerate token
        </Button>
      </Modal.Footer>
    </Modal.Content>
  </Modal.Root>
);

const ApiTokenSection = () => {
  const { alertToast } = useToast();

  const [newToken, setNewToken] = useState<string | null>(null);
  const [isRevokeOpen, setIsRevokeOpen] = useState(false);
  const [isRegenerateOpen, setIsRegenerateOpen] = useState(false);

  const { data, isLoading } = useGetApiTokenQuery();

  const [generateToken, { isLoading: isGenerating }] =
    useGenerateApiTokenMutation();

  const [revokeToken, { isLoading: isRevoking }] = useRevokeApiTokenMutation();

  const handleGenerate = async () => {
    try {
      const result = await generateToken().unwrap();
      setNewToken(result.token);
    } catch (err) {
      alertToast({
        message:
          (err as { data?: { message?: string } })?.data?.message ??
          "Failed to generate API token",
      });
    }
  };

  const handleRevoke = async () => {
    try {
      await revokeToken().unwrap();
      setIsRevokeOpen(false);
    } catch (err) {
      alertToast({
        message:
          (err as { data?: { message?: string } })?.data?.message ??
          "Failed to revoke API token",
      });
    }
  };

  const handleRegenerate = async () => {
    try {
      const result = await generateToken().unwrap();
      setNewToken(result.token);
      setIsRegenerateOpen(false);
    } catch (err) {
      alertToast({
        message:
          (err as { data?: { message?: string } })?.data?.message ??
          "Failed to regenerate API token",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="t-rounded-xl t-border t-border-solid t-border-neutral-10 t-bg-white t-p-6">
        <div className="t-h-5 t-w-48 t-animate-pulse t-rounded t-bg-neutral-10" />
      </div>
    );
  }

  const hasToken = data?.has_token;
  const isExpired = hasToken && data.is_expired;

  return (
    <div className="t-flex t-flex-col t-gap-4">
      {newToken && (
        <GeneratedTokenBanner
          token={newToken}
          onDismiss={() => setNewToken(null)}
        />
      )}

      <div className="t-rounded-xl t-border t-border-solid t-border-neutral-10 t-bg-white t-p-6">
        <div className="t-flex t-flex-col t-gap-5">
          <div className="t-flex t-flex-col t-gap-1">
            <div className="t-text-body-sm t-font-semibold t-text-text-100">
              API Token
            </div>
            <div className="t-text-body-sm t-text-text-30">
              Use this token to authenticate API requests. Keep it secret —
              treat it like a password.
            </div>
          </div>

          {hasToken && data.has_token ? (
            <div className="t-flex t-flex-col t-gap-4">
              {isExpired && (
                <div className="t-rounded-lg t-border t-border-solid t-border-red-20 t-bg-red-0 t-px-4 t-py-3 t-text-body-sm t-text-red">
                  This token expired on{" "}
                  {dayjs(data.expiry).format(DISPLAY_DATE)}. Regenerate to
                  continue using the API.
                </div>
              )}
              <div className="t-flex t-flex-col t-gap-2 t-rounded-lg t-border t-border-solid t-border-neutral-10 t-bg-surface-lighter-grey t-px-4 t-py-3">
                <div className="t-flex t-items-center t-justify-between">
                  <span className="t-font-mono t-text-body-sm t-text-text-60">
                    {data.token_prefix}
                    {TOKEN_PLACEHOLDER}
                  </span>
                </div>
                <div className="t-flex t-gap-4 t-text-body-sm t-text-text-30">
                  <span>
                    Created {dayjs(data.created_at).format(DISPLAY_DATE)}
                  </span>
                  {data.last_used_at && (
                    <span>
                      Last used {dayjs(data.last_used_at).format(DISPLAY_DATE)}
                    </span>
                  )}
                  <span className={isExpired ? "t-text-red" : ""}>
                    Expires {dayjs(data.expiry).format(DISPLAY_DATE)}
                  </span>
                </div>
              </div>
              <div className="t-flex t-gap-3">
                <Button
                  customType="secondary"
                  size="small"
                  onClick={() => setIsRegenerateOpen(true)}
                  type="button"
                >
                  Regenerate
                </Button>
                <Button
                  customType="danger"
                  size="small"
                  onClick={() => setIsRevokeOpen(true)}
                  type="button"
                >
                  Revoke
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <Button
                customType="primary"
                size="small"
                onClick={handleGenerate}
                isLoading={isGenerating}
                disabled={isGenerating}
                type="button"
              >
                Generate API Token
              </Button>
            </div>
          )}
        </div>
      </div>

      <RevokeConfirmModal
        open={isRevokeOpen}
        onOpenChange={setIsRevokeOpen}
        onConfirm={handleRevoke}
        isLoading={isRevoking}
      />

      <RegenerateConfirmModal
        open={isRegenerateOpen}
        onOpenChange={setIsRegenerateOpen}
        onConfirm={handleRegenerate}
        isLoading={isGenerating}
      />
    </div>
  );
};

export const ApiSettings = () => {
  usePageTitle("API Settings");

  return (
    <DashboardLayout header={<Header v2 title="API Settings" />}>
      <div className="t-w-[680px] t-flex t-flex-col t-gap-6">
        <div className="t-text-body-sm t-text-text-30">
          Manage your API token for programmatic access to your accounting data.
        </div>
        <ApiTokenSection />
      </div>
    </DashboardLayout>
  );
};
